import * as THREE from 'three'
import {
  defaultAnchorSpec,
  liftHand,
  OneEuroFilter,
  OneEuroVector,
  quat,
  rescaleHandDepth,
  vec3,
  PoseSmoother,
  solveRing,
  TrackingState,
  type AnchorSpec,
  type CameraModel,
  type HandFrame,
  type HandMetric,
  type POSE_SMOOTHER_PRESETS,
  type PoseSmootherConfig,
  type RingSolveResult,
} from '@ar-suite/core'
import { applyPose, ARScene, applyMaterialOverrides, disposeObject, HandOccluder } from '@ar-suite/render-web'

export interface RingModuleOptions {
  spec?: AnchorSpec
  smoothing?: PoseSmootherConfig | keyof typeof POSE_SMOOTHER_PRESETS
}

/**
 * Ring module: metric hand lift (core) → ring solver → smoother → three.js,
 * with a per-bone hand occluder so neighbouring fingers hide the band correctly.
 */
export class RingModule {
  readonly root = new THREE.Group()
  readonly smoother: PoseSmoother
  readonly occluder = new HandOccluder()
  readonly tracking = new TrackingState({ foundAfterFrames: 2, holdMs: 350, searchAfterMs: 1000 })
  private _spec: AnchorSpec
  private model: THREE.Object3D | null = null
  private metric: HandMetric | null = null
  /** Depth is the noisiest output of the weak-perspective fit — it gets its own strong smoothing. */
  private depthFilter = new OneEuroFilter({ minCutoff: 0.6, beta: 0.03 })
  /**
   * Landmark-level smoothing (63 One-Euro filters each for image and world coords) BEFORE solving:
   * direction estimates (finger axis, dorsal, forearm) amplify raw landmark noise into rotation
   * wobble that no amount of output-pose smoothing can cleanly remove.
   */
  private lmFilter = new OneEuroVector(63, { minCutoff: 1.2, beta: 0.9 })
  private wlFilter = new OneEuroVector(63, { minCutoff: 0.8, beta: 0.3 })
  private smoothFrame: HandFrame | null = null
  private lastHandedness: 'left' | 'right' | null = null
  /** Dorsal-sign hysteresis: the thumb-side estimate must disagree for several frames to flip. */
  private lastDorsal: [number, number, number] | null = null
  private dorsalFlipFrames = 0
  private result: RingSolveResult | null = null

  constructor(readonly scene: ARScene, options: RingModuleOptions = {}) {
    this._spec = options.spec ?? defaultAnchorSpec('ring')
    this.smoother = new PoseSmoother(options.smoothing ?? 'balanced')
    this.root.name = 'ring'
    this.root.visible = false
    scene.content.add(this.root)
    scene.occluders.add(this.occluder.group)
  }

  get spec(): AnchorSpec {
    return this._spec
  }
  get lastResult(): RingSolveResult | null {
    return this.result
  }
  get lastHand(): HandMetric | null {
    return this.metric
  }
  get hasModel(): boolean {
    return this.model !== null
  }
  set visible(v: boolean) {
    this.root.visible = v
    if (!v) this.occluder.update(null)
  }

  setSpec(spec: AnchorSpec): void {
    this._spec = spec
  }
  patchSpec(patch: Partial<AnchorSpec>): void {
    Object.assign(this._spec, patch)
  }
  setSmoothing(cfg: PoseSmootherConfig | keyof typeof POSE_SMOOTHER_PRESETS): void {
    this.smoother.configure(cfg)
  }

  setModel(model: THREE.Object3D | null): void {
    if (this.model) {
      this.root.remove(this.model)
      disposeObject(this.model)
    }
    this.model = model
    if (model) this.root.add(model)
    if (!model) this.occluder.update(null)
  }

  applyVariantMaterials(overrides: Record<string, { color?: string; metalness?: number; roughness?: number; opacity?: number }>): void {
    if (this.model) applyMaterialOverrides(this.model, overrides)
  }

  /** Pick the tracked hand by GEOMETRIC handedness ('any' = best score); returns the raw frame. */
  private pickHand(hands: HandFrame[], cam: CameraModel): { frame: HandFrame; handedness: 'left' | 'right' } | null {
    const want = this._spec.ring?.hand ?? 'any'
    let best: { frame: HandFrame; handedness: 'left' | 'right'; score: number } | null = null
    for (const h of hands) {
      const m = liftHand(h, cam)
      if (!m) continue
      if (want !== 'any' && m.handedness !== want) continue
      if (!best || h.score > best.score) best = { frame: h, handedness: m.handedness, score: h.score }
    }
    return best
  }

  /** One-Euro every landmark (image + world) into a scratch frame; filters reset on hand switch. */
  private smoothLandmarks(raw: HandFrame, handedness: 'left' | 'right', tMs: number): HandFrame {
    if (this.lastHandedness !== handedness) {
      this.lmFilter.reset()
      this.wlFilter.reset()
      this.lastHandedness = handedness
    }
    if (!this.smoothFrame || this.smoothFrame.landmarks.length !== raw.landmarks.length) {
      this.smoothFrame = {
        ...raw,
        landmarks: new Float32Array(raw.landmarks.length),
        worldLandmarks: raw.worldLandmarks ? new Float32Array(raw.worldLandmarks.length) : undefined,
      }
    }
    const t = tMs / 1000
    this.lmFilter.filter(this.smoothFrame.landmarks, raw.landmarks, t)
    if (raw.worldLandmarks && this.smoothFrame.worldLandmarks)
      this.wlFilter.filter(this.smoothFrame.worldLandmarks as Float32Array, raw.worldLandmarks, t)
    this.smoothFrame.handedness = raw.handedness
    this.smoothFrame.score = raw.score
    this.smoothFrame.width = raw.width
    this.smoothFrame.height = raw.height
    this.smoothFrame.timestamp = raw.timestamp
    return this.smoothFrame
  }

  /** Flip-hysteresis on the geometric dorsal: needs 5 consecutive contradicting frames to flip. */
  private stabilizeDorsal(m: HandMetric): void {
    const d: [number, number, number] = [0, 0, 0]
    quat.rotateVec3(d, m.frame.rotation, [0, 0, 1])
    if (this.lastDorsal && vec3.dot(d, this.lastDorsal) < 0) {
      this.dorsalFlipFrames++
      if (this.dorsalFlipFrames < 5) {
        // override: rotate 180° about the finger axis to restore the previous dorsal side
        const y: [number, number, number] = [0, 0, 0]
        quat.rotateVec3(y, m.frame.rotation, [0, 1, 0])
        const flip: [number, number, number, number] = [0, 0, 0, 1]
        quat.fromAxisAngle(flip, y, Math.PI)
        quat.multiply(m.frame.rotation, flip, m.frame.rotation)
        quat.rotateVec3(d, m.frame.rotation, [0, 0, 1])
        m.handedness = m.handedness === 'left' ? 'right' : 'left'
      } else {
        this.dorsalFlipFrames = 0 // accept the new side
      }
    } else {
      this.dorsalFlipFrames = 0
    }
    this.lastDorsal = d
  }

  update(hands: HandFrame[], cam: CameraModel, tMs: number): RingSolveResult | null {
    const picked = this.pickHand(hands, cam)
    const snap = this.tracking.update(!!picked, picked ? 1 : 0, tMs)
    if (snap.status !== 'tracking') {
      this.root.visible = false
      this.occluder.update(null)
      this.smoother.reset()
      this.depthFilter.reset()
      this.lmFilter.reset()
      this.wlFilter.reset()
      this.lastHandedness = null
      this.lastDorsal = null
      return null
    }
    if (!picked) return this.result // hold window: keep the last rendered state, no flicker
    const smoothed_frame = this.smoothLandmarks(picked.frame, picked.handedness, tMs)
    this.metric = liftHand(smoothed_frame, cam, this.metric ?? undefined)
    if (!this.metric) return this.result
    // depth stabilization: rescale the whole hand along its rays to the smoothed depth
    rescaleHandDepth(this.metric, this.depthFilter.filter(this.metric.depth, tMs / 1000))
    this.stabilizeDorsal(this.metric)
    this.result = solveRing(this.metric, this._spec.ring?.finger ?? 'ring', this._spec, 0.3, this.result ?? undefined)
    const smoothed = this.smoother.update(this.result.pose, tMs)
    applyPose(this.root, smoothed)
    this.root.visible = true
    // forearm continues opposite the fingers — occludes rings when the hand tilts away
    const fore: [number, number, number] = [0, 0, 0]
    quat.rotateVec3(fore, this.metric.frame.rotation, [0, -1, 0])
    this.occluder.update(this.metric, fore)
    return this.result
  }

  onTrackingLost(): void {
    this.root.visible = false
    this.occluder.update(null)
    this.smoother.reset()
    this.tracking.reset()
  }

  dispose(): void {
    this.setModel(null)
    this.scene.content.remove(this.root)
    this.scene.occluders.remove(this.occluder.group)
    this.occluder.dispose()
  }
}
